// src/components/navigation/ProfileDropdown.tsx

import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@glucose-care/shared";
import { Loader2, LogOut, UserIcon } from "lucide-react";

import type { UserDisplayInfo } from "../../types/navbar.types";

interface ProfileDropdownProps {
  userDisplayInfo: UserDisplayInfo;
  isLoggingOut: boolean;
  onNavigate: (path: string) => void;
  onLogout: () => void;
}

interface UserAvatarProps {
  avatar: string | null;
  initials: string;
  displayName: string;
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  avatar,
  initials,
  displayName,
}) => {
  const [imageFailed, setImageFailed] =
    React.useState(false);

  const showImage = Boolean(avatar) && !imageFailed;

  return (
    <div
      className="
        flex h-8 w-8 shrink-0 items-center
        justify-center overflow-hidden rounded-full
        bg-[#E0F7F4]
      "
    >
      {showImage ? (
        <img
          src={avatar ?? ""}
          alt={`${displayName} profile`}
          className="h-full w-full object-cover"
          loading="lazy"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="text-xs font-semibold text-[#117E78]">
          {initials}
        </span>
      )}
    </div>
  );
};

export const ProfileDropdown: React.FC<
  ProfileDropdownProps
> = ({
  userDisplayInfo,
  isLoggingOut,
  onNavigate,
  onLogout,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Open account menu"
          className="
            flex h-10 w-10 items-center justify-center
            rounded-full
            transition-colors
            hover:bg-[#F7FAF9]
            focus-visible:outline-none
            focus-visible:ring-2
            focus-visible:ring-[#159A92]
            focus-visible:ring-offset-2
          "
        >
          <UserAvatar
            avatar={userDisplayInfo.avatar}
            initials={userDisplayInfo.initials}
            displayName={userDisplayInfo.displayName}
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="
          w-60 rounded-lg border border-[#E4EFED]
          bg-white p-1 shadow-lg
        "
      >
        <DropdownMenuLabel className="px-3 py-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-[#17313A]">
              {userDisplayInfo.displayName}
            </p>

            <p className="mt-0.5 truncate text-xs font-normal text-[#65777C]">
              {userDisplayInfo.displayEmail}
            </p>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator className="bg-[#E4EFED]" />

        <DropdownMenuItem
          onSelect={() => onNavigate("/profile")}
          className="
            cursor-pointer rounded-md px-3 py-2
            text-sm text-[#52666C]
            focus:bg-[#F0F8F7]
            focus:text-[#17313A]
          "
        >
          <UserIcon
            className="mr-3 h-4 w-4 text-[#7D9094]"
            aria-hidden="true"
          />

          Facility profile
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-[#E4EFED]" />

        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault();

            if (!isLoggingOut) {
              onLogout();
            }
          }}
          disabled={isLoggingOut}
          className="
            cursor-pointer rounded-md px-3 py-2
            text-sm text-[#C94F4F]
            focus:bg-[#FDECEC]
            focus:text-[#C94F4F]
            data-[disabled]:cursor-not-allowed
            data-[disabled]:opacity-50
          "
        >
          {isLoggingOut ? (
            <Loader2
              className="mr-3 h-4 w-4 animate-spin"
              aria-hidden="true"
            />
          ) : (
            <LogOut
              className="mr-3 h-4 w-4"
              aria-hidden="true"
            />
          )}

          {isLoggingOut ? "Signing out…" : "Sign out"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProfileDropdown;